// lib
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import HeaderProfilesDirectory from 'src/containers/smart_components/header/header_profiles_directory'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import ProfilesDirectoryCategory from 'src/containers/smart_components/profiles_directory/profiles_directory_category'

// constants & interfaces
import { PROFILES_DIRECTORY } from 'src/constants/feed'
import {
  PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_AUTHORITY_PROFILES,
  PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_PRESS_PROFILES,
  PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_PRIVATE_PROFILES,
  PROFILES_DIRECTORY_CATEGORY_TITLE_AUTHORITY_PROFILES,
  PROFILES_DIRECTORY_CATEGORY_TITLE_PRESS_PROFILES,
  PROFILES_DIRECTORY_CATEGORY_TITLE_PRIVATE_PROFILES
} from 'src/constants/meta_tags'
import { textResources } from 'src/lang/de'

// helpers
import { ActorProfileType, ProfileSortingKeys, SortDirection, useProfiles } from '@lopo-tech/lopo-api'
import { useAuthorTypeFromURL } from 'src/actions/profiles_list/profiles_list_hooks'
import { usePageTracking } from 'src/pages/hooks/hooks'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { getPageTitle } from 'src/utils/page_title/page_title'
import { ISelectOption } from '../components/forms/select-field/select-field'
import { ProfilesListProfileType } from '../interfaces/profile'
import { LayoutFeaturedContainer } from '../layout/layout_featured_container'
import { createMarkersList } from '../utils/feeds'
const labels = textResources.profileList

export interface IOrderByOption extends ISelectOption {
  value: ProfileSortingKeys;
}

const orderByOptions: IOrderByOption[] = [
  {
    text: labels.orderByAccessionDate_WEB,
    value: ProfileSortingKeys.CreatedAt
  },
  {
    text: labels.orderByName,
    value: ProfileSortingKeys.Name
  }
]

const mapListProfileType = (listProfileType: ProfilesListProfileType | undefined): ActorProfileType | undefined => {
  switch (listProfileType) {
    case 'AuthorityProfile':
      return ActorProfileType.AuthorityProfile
    case 'BlogProfile':
      return ActorProfileType.BlogProfile
    case 'PressProfile':
      return ActorProfileType.PressProfile
    case 'PrivateProfile':
      return ActorProfileType.PrivateProfile
    default:
      return undefined
  }
}

const ProfilesDirectoryCategoryPage: React.FC<RouteComponentProps> = () => {
  const locationShape = useGetAppLocationShape()
  const user = useSelectUser()
  const authorType = useAuthorTypeFromURL()

  let title = ''
  let description = ''
  if (locationShape) {
    switch (authorType) {
      case 'PressProfile': {
        title = PROFILES_DIRECTORY_CATEGORY_TITLE_PRESS_PROFILES(locationShape.name)
        description = PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_PRESS_PROFILES(locationShape.name)
        break
      }
      case 'PrivateProfile': {
        title = PROFILES_DIRECTORY_CATEGORY_TITLE_PRIVATE_PROFILES(locationShape.name)
        description = PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_PRIVATE_PROFILES(locationShape.name)
        break
      }
      default: {
        title = PROFILES_DIRECTORY_CATEGORY_TITLE_AUTHORITY_PROFILES(locationShape.name)
        description = PROFILES_DIRECTORY_CATEGORY_DESCRIPTION_AUTHORITY_PROFILES(locationShape.name)
      }
    }
  }

  const profileType = mapListProfileType(authorType)

  const [orderBy, setOrderBy] = useState(ProfileSortingKeys.Name)

  const {
    data: profilesData,
    loading,
    fetchMore: fetchMoreProfiles
  } = useProfiles({
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      types: [profileType!],
      page: 1,
      perPage: 20,
      sorting: {
        // TODO: Currently not possible to update sort direction in UI
        direction: SortDirection.Desc,
        fieldName: orderBy
      }
    },
    skip: !locationShape || !profileType
  })

  const profiles = profilesData?.profiles
  const profilesList = profiles?.items ?? []

  const currentPage = profiles?.pageInfo.previousPage ?? 0 + 1
  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    const nextPage = profiles?.pageInfo.nextPage
    if (nextPage && !loadingRef.current) {
      fetchMoreProfiles({ variables: { page: nextPage } })
    }
  }, [profiles?.pageInfo.nextPage, fetchMoreProfiles])

  const markers = createMarkersList(profilesList)

  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set())

  const onVisibleChange = useCallback((id: string, inView: boolean) => {
    setVisibleIds(prev => {
      const next = new Set(prev)
      if (inView) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const markersInView = useMemo(
    () => markers.filter(m => visibleIds.has(m.id)),
    [markers, visibleIds]
  )

  const onChange = useCallback((val) => setOrderBy(val as ProfileSortingKeys), [])

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags
        description={description}
        title={title}
      />
      <LayoutFeaturedContainer
        markers={markersInView}
        locationShape={locationShape}
        activeNavigationItem={PROFILES_DIRECTORY}
        pageTitle={
          <StandardPageTitle
            text={getPageTitle(textResources.pageTitles.profileSearchSimple, textResources.pageTitles.profileSearch)}
            showLocation
          />
        }
        subHeader={<HeaderProfilesDirectory />}
        withHeaderMargin>
        <ProfilesDirectoryCategory
          currentPage={currentPage}
          getNextPage={getNextPage}
          loading={loading}
          profilesListData={profilesList}
          onVisibleChange={onVisibleChange}
          orderBy={orderBy}
          onChange={onChange}
          orderByOptions={orderByOptions}
        />
      </LayoutFeaturedContainer>
    </>
  )
}

export default ProfilesDirectoryCategoryPage
